import React, { useEffect, useState } from "react";
import { useLang } from "../../context/LangContext";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { TrendingUp, CreditCard, CheckCircle2, XCircle, Clock, Wallet, BarChart3, Users, HandCoins } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

const OMR = (n) => `${Number(n || 0).toFixed(2)} OMR`;

const STATUS_STYLE = {
  paid: { cls: "bg-[#E6F1E7] text-[#2A5948]", icon: CheckCircle2 },
  pending: { cls: "bg-[#FFF3D6] text-[#8A6D1F]", icon: Clock },
  failed: { cls: "bg-[#FBE9E4] text-[#C96A52]", icon: XCircle },
};

export default function AdminPayments() {
  const { t } = useLang();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/payments/analytics").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  return (
    <div className="space-y-6" data-testid="admin-payments-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold flex items-center gap-2">
          <HandCoins className="text-[#2A5948]" size={26} />
          {t("payments_analytics")}
        </h1>
      </div>

      {/* Revenue stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card testid="rev-all" icon={TrendingUp} label={t("total_revenue")} value={OMR(data?.totals.all_time)} tone="brand" />
        <Card testid="rev-month" icon={Wallet} label={t("revenue_this_month")} value={OMR(data?.totals.this_month)} tone="green" />
        <Card testid="rev-week" icon={Wallet} label={t("revenue_last_7")} value={OMR(data?.totals.last_7_days)} tone="accent" />
        <Card testid="rev-today" icon={Wallet} label={t("revenue_today")} value={OMR(data?.totals.today)} tone="green" />
      </div>

      {/* Subscription + tx counts */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card testid="subs-active" icon={Users} label={t("active_subscriptions")} value={data?.subscriptions.active ?? "—"} tone="green" />
        <Card testid="subs-expired" icon={Users} label={t("expired_subscriptions")} value={data?.subscriptions.expired ?? "—"} tone="red" />
        <Card testid="txs-successful" icon={CheckCircle2} label={t("successful_txs")} value={data?.counts.successful ?? "—"} tone="green" />
        <Card testid="txs-pending" icon={Clock} label={t("pending_txs")} value={data?.counts.pending ?? "—"} tone="accent" />
      </div>

      {/* Monthly revenue chart */}
      <div className="bg-white border border-[#E5E1D8] rounded-2xl p-4 sm:p-6 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <BarChart3 size={16} className="text-[#2A5948]" />
          <h3 className="font-semibold">{t("monthly_revenue")}</h3>
        </div>
        <div className="w-full h-72">
          <ResponsiveContainer>
            <BarChart data={data?.monthly_chart || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E1D8" />
              <XAxis dataKey="month" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => `${Number(v).toFixed(2)} OMR`} />
              <Bar dataKey="amount" fill="#2A5948" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Transactions table */}
      <div className="bg-white border border-[#E5E1D8] rounded-2xl shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-[#E5E1D8]">
          <h3 className="font-semibold flex items-center gap-2">
            <CreditCard size={16} className="text-[#2A5948]" />
            {t("recent_transactions")}
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F3EFE7] text-[#5C6053] text-xs uppercase">
              <tr>
                <th className="text-start px-4 py-3">{t("tx_date")}</th>
                <th className="text-start px-4 py-3">{t("tx_user")}</th>
                <th className="text-start px-4 py-3">{t("tx_package")}</th>
                <th className="text-start px-4 py-3">{t("tx_amount")}</th>
                <th className="text-start px-4 py-3">{t("tx_type")}</th>
                <th className="text-start px-4 py-3">{t("tx_status")}</th>
                <th className="text-start px-4 py-3">{t("tx_order_id")}</th>
              </tr>
            </thead>
            <tbody>
              {(!data || data.recent_transactions.length === 0) && (
                <tr><td colSpan={7} className="text-center py-8 text-[#5C6053]">—</td></tr>
              )}
              {data?.recent_transactions.map((tx) => {
                const key = tx.payment_status === "paid" ? "paid" : tx.payment_status === "failed" ? "failed" : "pending";
                const s = STATUS_STYLE[key];
                const Icon = s.icon;
                return (
                  <tr key={tx.id} data-testid={`tx-${tx.id}`} className="border-t border-[#E5E1D8]">
                    <td className="px-4 py-3 text-xs text-[#5C6053] whitespace-nowrap">
                      {tx.created_at?.slice(0, 16).replace("T", " ")}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm">{tx.user_name || "—"}</div>
                      <div className="text-xs text-[#5C6053]" dir="ltr">{tx.user_phone || ""}</div>
                    </td>
                    <td className="px-4 py-3 text-xs">{tx.package_id || "—"}</td>
                    <td className="px-4 py-3 font-medium">{OMR(tx.amount)}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] px-2 py-0.5 rounded ${tx.manual_grant ? "bg-[#FBE9E4] text-[#C96A52]" : "bg-[#E6F1E7] text-[#2A5948]"}`}>
                        {tx.manual_grant ? t("tx_manual") : t("tx_ompay")}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded ${s.cls}`}>
                        <Icon size={10} /> {t(`tx_status_${key}`)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-[#5C6053] font-mono truncate max-w-[180px]" dir="ltr">
                      {tx.order_id || "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Card({ icon: Icon, label, value, tone, testid }) {
  const map = {
    green: "bg-[#E6F1E7] text-[#2A5948]",
    red: "bg-[#FBE9E4] text-[#A85540]",
    brand: "bg-[#2A5948] text-white",
    accent: "bg-[#FFF3D6] text-[#8A6D1F]",
  };
  return (
    <div data-testid={testid} className="bg-white border border-[#E5E1D8] rounded-2xl p-4 shadow-sm">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${map[tone]}`}>
        <Icon size={18} />
      </div>
      <div className="mt-3 text-xs text-[#5C6053]">{label}</div>
      <div className="mt-1 text-xl font-semibold">{value}</div>
    </div>
  );
}
